// src/pages/finance-portal/reports/IncomeStatement.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
    CircularProgress, Grid, Chip, Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatPercent,
    firstOfYearIso, todayIso, downloadCsv, printReport, AccountLink,
} from './reportShared';

export default function IncomeStatement() {
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [basis, setBasis] = useState('ACCRUAL');
    const [format, setFormat] = useState('STANDARD');
    const [comparePrior, setComparePrior] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/income-statement', {
                params: {
                    start_date: start,
                    end_date: end,
                    basis,
                    format,
                    compare_prior: comparePrior ? 1 : 0,
                },
            });
            if (res.data && res.data.success) {
                setData({
                    ...res.data,
                    revenue: Array.isArray(res.data.revenue) ? res.data.revenue : [],
                    expense: Array.isArray(res.data.expense) ? res.data.expense : [],
                });
            } else {
                setError(res.data?.message || 'Failed to load');
            }
        } catch (e) {
            console.error('Income Statement load error:', e);
            setError(e.response?.data?.message || e.message || 'Failed to load income statement');
        } finally {
            setLoading(false);
        }
    }, [start, end, basis, format, comparePrior]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            { section: 'Revenue' },
            ...data.revenue.map((r) => ({
                section: '',
                account: `${r.account_number} ${r.account_name}`,
                amount: r.balance,
                percent_of_revenue: r.percent_of_revenue ?? '',
            })),
            { section: 'Total Revenue', amount: data.total_revenue },
            { section: 'Expense' },
            ...data.expense.map((r) => ({
                section: '',
                account: `${r.account_number} ${r.account_name}`,
                amount: r.balance,
                percent_of_revenue: r.percent_of_revenue ?? '',
            })),
            { section: 'Total Expense', amount: data.total_expense },
            { section: 'Net Income', amount: data.net_income },
        ];
        downloadCsv(`income_statement_${start}_${end}.csv`, rows,
            ['section', 'account', 'amount', 'percent_of_revenue']);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <TrendingUpIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>
                    Income Statement (P&amp;L)
                </Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Start Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="End Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Basis</InputLabel>
                            <Select label="Basis" value={basis} onChange={(e) => setBasis(e.target.value)}>
                                <MenuItem value="ACCRUAL">Accrual</MenuItem>
                                <MenuItem value="CASH">Cash</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Format</InputLabel>
                            <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value)}>
                                <MenuItem value="STANDARD">Standard</MenuItem>
                                <MenuItem value="COMMON_SIZE">Common-size</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControlLabel
                            control={<Checkbox checked={comparePrior}
                                onChange={(e) => setComparePrior(e.target.checked)} />}
                            label="Compare prior"
                        />
                    </Grid>
                    <Grid item xs={6} md={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>
                            Run
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 3 }} id="is-report">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Income Statement</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {data.period?.start_date} – {data.period?.end_date} · {data.basis}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<PrintIcon />} onClick={() => printReport('is-report')}>Print</Button>
                            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    {comparePrior && <TableCell align="right">Prior</TableCell>}
                                    {format === 'COMMON_SIZE' && <TableCell align="right">% of Rev</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>REVENUE</TableCell>
                                </TableRow>
                                {(data.revenue || []).map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>
                                            <AccountLink
                                                accountId={r.account_id}
                                                label={`${r.account_number} — ${r.account_name}`}
                                                startDate={start}
                                                endDate={end}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(r.balance)}</TableCell>
                                        {comparePrior && <TableCell align="right" />}
                                        {format === 'COMMON_SIZE' && (
                                            <TableCell align="right">{formatPercent(r.percent_of_revenue || 0)}</TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Total Revenue</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_revenue)}</TableCell>
                                    {comparePrior && <TableCell align="right">
                                        {formatCurrency(data.prior_period?.total_revenue)}</TableCell>}
                                    {format === 'COMMON_SIZE' && <TableCell align="right">100.00%</TableCell>}
                                </TableRow>

                                <TableRow>
                                    <TableCell colSpan={4} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>EXPENSE</TableCell>
                                </TableRow>
                                {(data.expense || []).map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>
                                            <AccountLink
                                                accountId={r.account_id}
                                                label={`${r.account_number} — ${r.account_name}`}
                                                startDate={start}
                                                endDate={end}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(r.balance)}</TableCell>
                                        {comparePrior && <TableCell align="right" />}
                                        {format === 'COMMON_SIZE' && (
                                            <TableCell align="right">{formatPercent(r.percent_of_revenue || 0)}</TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Total Expense</TableCell>
                                    <TableCell align="right">{formatCurrency(data.total_expense)}</TableCell>
                                    {comparePrior && <TableCell align="right">
                                        {formatCurrency(data.prior_period?.total_expense)}</TableCell>}
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>

                                <TableRow sx={{ '& td': { borderTop: '3px double #111827', fontWeight: 700, fontSize: '1.05em' } }}>
                                    <TableCell>Net Income</TableCell>
                                    <TableCell align="right"
                                        sx={{ color: data.net_income >= 0 ? ACCENT : '#ef4444' }}>
                                        {formatCurrency(data.net_income)}
                                    </TableCell>
                                    {comparePrior && <TableCell align="right">
                                        {formatCurrency(data.prior_period?.net_income)}</TableCell>}
                                    {format === 'COMMON_SIZE' && <TableCell align="right" />}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Chip label={`Gross margin: ${formatPercent(data.gross_margin_percent)}`} />
                        <Chip label={`Basis: ${data.basis}`} variant="outlined" />
                    </Stack>
                </Paper>
            )}
        </Box>
    );
}
